import { create } from 'zustand';
import type { VerifiedDomainInstance } from '@/types/domain';
import type { AuthUser, AuthProductAccess } from '@/types/auth';
import { AUTH_STORAGE_KEYS } from '@/lib/api/config';
import { clearAuthStorage } from '@/lib/api/auth-client';

export type UserRole =
  | 'admin'
  | 'hr_manager'
  | 'finance_lead'
  | 'department_head'
  | 'manager'
  | 'employee'
  | 'recruiter'
  | 'auditor';

export interface DisplayUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  orgId: string;
  authRole: string;
  isSuperAdmin: boolean;
  permissions: string[];
  /** From JWT HRM product — functional role code (HR, FINANCE, …). */
  roleCode?: string;
  levelRank?: number;
  levelName?: string;
  /** Human label e.g. "HR Senior" from dashboard config / JWT. */
  variantLabel?: string;
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  displayUser: DisplayUser | null;
  accessToken: string | null;
  sessionId: string | null;
  /** Friendly label for this browser/device from auth-service UA parsing. */
  deviceName: string | null;
  orgCode: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  verifiedInstance: VerifiedDomainInstance | null;
  /**
   * Session role preview: UI + backend guards use this role's exact permissions
   * (auth-service Redis overlay synced via POST /auth/me/role-preview).
   */
  rolePreview: RolePreview | null;
  setVerifiedInstance: (instance: VerifiedDomainInstance | null) => void;
  setAuthSession: (input: {
    user: AuthUser;
    accessToken: string;
    sessionId: string;
    orgCode: string;
    deviceName?: string | null;
  }) => void;
  setUserFromToken: (user: AuthUser) => void;
  patchUserProfileMedia: (media: {
    profileImageUrl?: string | null;
    coverImageUrl?: string | null;
  }) => void;
  /** Refresh HRM permission codes after role changes or token refresh. */
  syncPermissions: (products: AuthProductAccess[]) => void;
  setRolePreview: (preview: RolePreview | null) => void;
  clearRolePreview: () => void;
  setInitializing: (value: boolean) => void;
  logout: () => void;
}

export type RolePreview = {
  roleId: string;
  roleName: string;
  /** Optional rank label e.g. "Senior" */
  levelName?: string | null;
  levelId?: string | null;
  permissions: string[];
  roleCode?: string | null;
};

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  hr_manager: 'HR Manager',
  finance_lead: 'Finance Lead',
  department_head: 'Department Head',
  manager: 'Manager',
  employee: 'Employee',
  recruiter: 'Recruiter',
  auditor: 'Auditor',
};

export const ROLE_AVATARS: Record<UserRole, { color: string; icon: string }> = {
  admin: { color: 'from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)]', icon: 'ri-shield-star-fill' },
  hr_manager: { color: 'from-blue-500 to-blue-600', icon: 'ri-user-settings-fill' },
  finance_lead: { color: 'from-emerald-500 to-emerald-600', icon: 'ri-money-dollar-circle-fill' },
  department_head: { color: 'from-amber-500 to-amber-600', icon: 'ri-team-fill' },
  manager: { color: 'from-purple-500 to-purple-600', icon: 'ri-user-star-fill' },
  employee: { color: 'from-cyan-500 to-cyan-600', icon: 'ri-user-fill' },
  recruiter: { color: 'from-indigo-500 to-indigo-600', icon: 'ri-user-search-fill' },
  auditor: { color: 'from-slate-500 to-slate-600', icon: 'ri-eye-fill' },
};

/**
 * Decode slim JWT product metadata (roleCode, levelRank — not permission codes).
 */
function decodeTokenProductMeta(
  token: string | null,
): AuthProductAccess[] {
  if (!token) return [];
  try {
    const payload = token.split('.')[1];
    if (!payload) return [];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = JSON.parse(json) as { products?: AuthProductAccess[] };
    return decoded.products ?? [];
  } catch {
    return [];
  }
}

function mergeProductAccess(
  withPermissions: AuthProductAccess[],
  meta?: AuthProductAccess[],
): AuthProductAccess[] {
  const byCode = new Map(withPermissions.map((p) => [p.code.toUpperCase(), { ...p }]));
  for (const m of meta ?? []) {
    const key = m.code.toUpperCase();
    const existing = byCode.get(key);
    if (existing) {
      byCode.set(key, {
        ...existing,
        ...m,
        permissions: existing.permissions ?? m.permissions ?? [],
      });
    } else {
      byCode.set(key, { ...m, permissions: m.permissions ?? [] });
    }
  }
  return [...byCode.values()];
}

/**
 * Infer a friendly display role from canonical HRM permissions. The platform
 * `authRole` (e.g. "member") does not reflect the HRM org role, so we derive a label
 * from what the user can actually do. Used only for the avatar/label — access control
 * is permission-driven, not role-name driven.
 */
function inferRoleFromPermissions(
  permissions: string[],
  authRole: string,
  isSuperAdmin: boolean,
): UserRole {
  if (isSuperAdmin) return 'admin';
  const set = new Set(permissions.map((p) => p.trim().toLowerCase()));
  const role = authRole.toLowerCase();
  const has = (c: string) => set.has(c);

  if (role.includes('admin') || (has('identity.users.update') && has('platform.settings.read'))) {
    return 'admin';
  }
  if (has('employee.records.update') && has('leave.policy.read')) return 'hr_manager';
  if (has('payroll.run.update') || has('payroll.records.export')) return 'finance_lead';
  if (has('recruitment.openings.update')) return 'recruiter';
  if (has('compliance.audit.read') && !has('employee.records.update')) return 'auditor';
  if (has('leave.requests.delete') || has('performance.reviews.export')) return 'manager';
  return 'employee';
}

function buildDisplayUser(
  user: AuthUser,
  products: AuthProductAccess[],
): DisplayUser {
  const hrm = products.find((p) => p.code.toUpperCase() === 'HRM');
  const permissions = hrm?.permissions ?? [];
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email;
  const roleCode = hrm?.roleCode;
  const levelName = hrm?.levelName;
  const variantLabel =
    roleCode && levelName ? `${roleCode.replace(/_/g, ' ')} ${levelName}`.trim() : undefined;

  return {
    id: user.id,
    name,
    email: user.email,
    role: inferRoleFromPermissions(permissions, user.role, user.isSuperAdmin),
    orgId: user.orgId,
    authRole: user.role,
    isSuperAdmin: user.isSuperAdmin,
    permissions,
    roleCode,
    levelRank: hrm?.levelRank,
    levelName,
    variantLabel,
    profileImageUrl: user.profileImageUrl ?? null,
    coverImageUrl: user.coverImageUrl ?? null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  displayUser: null,
  accessToken: null,
  sessionId: null,
  deviceName: localStorage.getItem(AUTH_STORAGE_KEYS.deviceName),
  orgCode: localStorage.getItem(AUTH_STORAGE_KEYS.orgCode),
  isAuthenticated: false,
  isInitializing: Boolean(localStorage.getItem(AUTH_STORAGE_KEYS.accessToken)),
  verifiedInstance: null,
  rolePreview: null,

  setVerifiedInstance: (instance) => {
    if (instance?.code) {
      localStorage.setItem(AUTH_STORAGE_KEYS.domainCode, instance.code);
    }
    set({ verifiedInstance: instance });
  },

  setAuthSession: ({ user, accessToken, sessionId, orgCode, deviceName }) => {
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.sessionId, sessionId);
    localStorage.setItem(AUTH_STORAGE_KEYS.orgCode, orgCode);
    const resolvedDevice =
      deviceName != null && String(deviceName).length > 0 ? String(deviceName) : null;
    if (resolvedDevice) {
      localStorage.setItem(AUTH_STORAGE_KEYS.deviceName, resolvedDevice);
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEYS.deviceName);
    }
    const tokenMeta = decodeTokenProductMeta(accessToken);
    const products = mergeProductAccess(user.products ?? [], tokenMeta);
    const displayUser = buildDisplayUser({ ...user, products }, products);
    set({
      user: { ...user, products },
      displayUser,
      accessToken,
      sessionId,
      deviceName: resolvedDevice,
      orgCode,
      isAuthenticated: true,
      isInitializing: false,
      rolePreview: null,
    });
  },

  setUserFromToken: (user) => {
    const me = user as AuthUser & { products?: AuthProductAccess[] };
    const tokenMeta = decodeTokenProductMeta(localStorage.getItem(AUTH_STORAGE_KEYS.accessToken));
    const products = mergeProductAccess(me.products ?? [], tokenMeta);
    const userWithProducts = { ...me, products };
    set({
      user: userWithProducts,
      displayUser: buildDisplayUser(userWithProducts, products),
      accessToken: localStorage.getItem(AUTH_STORAGE_KEYS.accessToken),
      sessionId: localStorage.getItem(AUTH_STORAGE_KEYS.sessionId),
      deviceName: localStorage.getItem(AUTH_STORAGE_KEYS.deviceName),
      orgCode: localStorage.getItem(AUTH_STORAGE_KEYS.orgCode),
      isAuthenticated: true,
      isInitializing: false,
    });
  },

  syncPermissions: (products) =>
    set((state) => {
      if (!state.user) return state;
      const tokenMeta = decodeTokenProductMeta(state.accessToken);
      const merged = mergeProductAccess(products, tokenMeta);
      const user = { ...state.user, products: merged };
      return {
        user,
        displayUser: buildDisplayUser(user, merged),
      };
    }),

  setRolePreview: (preview) => set({ rolePreview: preview }),
  clearRolePreview: () => set({ rolePreview: null }),

  patchUserProfileMedia: (media) =>
    set((state) => {
      if (!state.user && !state.displayUser) return state;
      const user = state.user
        ? {
            ...state.user,
            ...(media.profileImageUrl !== undefined
              ? { profileImageUrl: media.profileImageUrl }
              : {}),
            ...(media.coverImageUrl !== undefined ? { coverImageUrl: media.coverImageUrl } : {}),
          }
        : null;
      const displayUser = state.displayUser
        ? {
            ...state.displayUser,
            ...(media.profileImageUrl !== undefined
              ? { profileImageUrl: media.profileImageUrl }
              : {}),
            ...(media.coverImageUrl !== undefined
              ? { coverImageUrl: media.coverImageUrl }
              : {}),
          }
        : null;
      return { user, displayUser };
    }),

  setInitializing: (value) => set({ isInitializing: value }),

  logout: () => {
    clearAuthStorage();
    localStorage.removeItem(AUTH_STORAGE_KEYS.orgCode);
    localStorage.removeItem(AUTH_STORAGE_KEYS.domainCode);
    set({
      user: null,
      displayUser: null,
      accessToken: null,
      sessionId: null,
      deviceName: null,
      orgCode: null,
      isAuthenticated: false,
      isInitializing: false,
      verifiedInstance: null,
      rolePreview: null,
    });
  },
}));
